import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createSelector } from 'reselect';
import { MonthPickerInput } from '@mantine/dates';
import {
	Alert,
	Button,
	Card,
	CardBody,
	CardHeader,
	Col,
	Input,
	Label,
	Row,
	Spinner,
	Table,
} from 'reactstrap';
import {
	getModulesList,
	getModuleUsageFactList,
	previewPayment as fetchPaymentPreview,
} from '../../../helpers/settings/backend_helper';
import {
	getCompanyList,
	getEstablishmentList,
	getEstablishmentStatusList,
} from '../../../helpers/establishment/backend_helper';
import { generatePaymentSchema } from '../Payment/generatePaymentSchema';
import { buildPaymentGenerateHref } from '../Payment/paymentGenerateQuery';
import { countHeaderUsed } from './countHeaderUsed';
import {
	filterRowsByStatus,
	groupRowsByCompany,
	mapStoredFacts,
} from './mapStoredFacts';
import {
	descriptionForRow,
	indexPreviewItems,
	itemForRow,
} from './matchPreviewItems';
import {
	previewErrorDetail,
	runFactsCalculatePreview,
} from './runFactsCalculatePreview';
import { UsedBadge, UsedLegend } from './UsedBadge';
import { EstablishmentStatusBadge } from './StatusBadge';
import { dateToPeriod, periodToDate } from './usagePeriod';

const selectPreviewState = createSelector(
	state => state.Payments,
	payments => ({
		paymentPreview: payments?.paymentPreview || null,
		paymentsError: payments?.error,
		isCalculating: payments?.isCalculating,
		isPreviewLoading: payments?.isPreviewLoading,
		isCalculateError: payments?.isCalculateError,
		isPreviewError: payments?.isPreviewError,
	}),
);

const asList = value => (Array.isArray(value) ? value : []);

const hasDuplicatePreviewNames = index => {
	if (!index || typeof index.values !== 'function') {
		return false;
	}
	for (const entry of index.values()) {
		if (entry && entry.status === 'duplicate') {
			return true;
		}
	}
	return false;
};

const ModuleUsageFactPreview = () => {
	const dispatch = useDispatch();
	const [year, setYear] = useState('');
	const [month, setMonth] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [hasConsulted, setHasConsulted] = useState(false);
	const [includeNop, setIncludeNop] = useState(false);
	const [facts, setFacts] = useState([]);
	const [modules, setModules] = useState([]);
	const [establishments, setEstablishments] = useState([]);
	const [statuses, setStatuses] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [includeSma, setIncludeSma] = useState(false);
	const [smaPreview, setSmaPreview] = useState(null);
	const [periodError, setPeriodError] = useState(null);
	const [refreshErrorDetail, setRefreshErrorDetail] = useState(null);
	const [previewStatus, setPreviewStatus] = useState('idle');
	const [previewData, setPreviewData] = useState(null);
	const previewRequestSeq = useRef(0);
	const {
		paymentPreview,
		paymentsError,
		isCalculating,
		isPreviewLoading,
		isCalculateError,
		isPreviewError,
	} = useSelector(selectPreviewState);

	const matrix = useMemo(
		() =>
			mapStoredFacts(facts, {
				modules,
				establishments,
				statuses,
				companies,
			}),
		[facts, modules, establishments, statuses, companies],
	);

	const nopStatus = statuses.find(status => status && status.code === 'NOP');
	const visibleRows = useMemo(
		() =>
			filterRowsByStatus(matrix.rows, {
				includeNop: Boolean(includeNop && nopStatus),
			}),
		[matrix.rows, includeNop, nopStatus],
	);
	const groups = useMemo(
		() => groupRowsByCompany(visibleRows),
		[visibleRows],
	);

	const previewIndex = useMemo(
		() =>
			previewStatus === 'ok' ? indexPreviewItems(previewData) : new Map(),
		[previewStatus, previewData],
	);
	const previewHasDuplicates = hasDuplicatePreviewNames(previewIndex);
	const headerCounts = useMemo(
		() => countHeaderUsed(matrix, establishments),
		[matrix, establishments],
	);

	const columnCount = matrix.columns.length + 4;
	const censusIncomplete =
		hasConsulted && matrix.census && matrix.census.complete === false;

	const periodValid = generatePaymentSchema.isValidSync({
		year: year === '' ? undefined : Number(year),
		month: month === '' ? undefined : Number(month),
	});
	const ritualBusy = isCalculating || isPreviewLoading;
	const errorDetail =
		(isCalculateError || isPreviewError) &&
		previewErrorDetail(paymentsError);

	const handleConsult = async () => {
		if (!year || !month) {
			return;
		}
		const seq = previewRequestSeq.current + 1;
		previewRequestSeq.current = seq;
		setPreviewStatus('loading');
		setPreviewData(null);
		fetchPaymentPreview({
			year: Number(year),
			month: Number(month),
			category: 'REGULAR',
		})
			.then(data => {
				if (seq !== previewRequestSeq.current) {
					return;
				}
				setPreviewData(data);
				setPreviewStatus('ok');
			})
			.catch(() => {
				if (seq !== previewRequestSeq.current) {
					return;
				}
				setPreviewData(null);
				setPreviewStatus('error');
			});
		setIsLoading(true);
		try {
			const [
				factsResponse,
				modulesResponse,
				establishmentsResponse,
				statusesResponse,
				companiesResponse,
			] = await Promise.all([
				getModuleUsageFactList({
					year: Number(year),
					month: Number(month),
					limit: 9999,
				}),
				getModulesList(),
				getEstablishmentList({ skip: 0, limit: 9999 }),
				getEstablishmentStatusList(),
				getCompanyList(),
			]);
			setFacts(asList(factsResponse));
			setModules(asList(modulesResponse));
			setEstablishments(asList(establishmentsResponse));
			setStatuses(asList(statusesResponse));
			setCompanies(asList(companiesResponse));
			setHasConsulted(true);
		} catch (error) {
			toast.error(
				error?.message || 'No se pudieron cargar los hechos de uso.',
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCalculate = async () => {
		let parsed;
		try {
			parsed = await generatePaymentSchema.validate({
				year: year === '' ? undefined : Number(year),
				month: month === '' ? undefined : Number(month),
				includeSma,
			});
			setPeriodError(null);
		} catch (error) {
			setPeriodError(
				error.message || 'El año y el mes son obligatorios'
			);
			return;
		}
		setSmaPreview(null);
		setRefreshErrorDetail(null);
		const result = await runFactsCalculatePreview({
			dispatch,
			previewPaymentApi: fetchPaymentPreview,
			refreshFacts: async ({ year: nextYear, month: nextMonth, limit }) => {
				const factsResponse = await getModuleUsageFactList({
					year: nextYear,
					month: nextMonth,
					limit,
				});
				setFacts(asList(factsResponse));
				setHasConsulted(true);
			},
			year: parsed.year,
			month: parsed.month,
			includeSma: parsed.includeSma,
		});
		setSmaPreview(result.smaPreview);
		setRefreshErrorDetail(result.refreshErrorDetail);
	};

	const billedDescription = rowName =>
		previewStatus === 'ok' ? descriptionForRow(previewIndex, rowName) : '';
	const billedItem = rowName =>
		previewStatus === 'ok' ? itemForRow(previewIndex, rowName) : undefined;

	return (
		<Card>
			<CardHeader className='border-0 pb-0'>
				<div className='d-flex align-items-center justify-content-between mb-2'>
					<div>
						<h5 className='card-title mb-0'>Hechos de uso guardados</h5>
						<small className='text-muted'>
							Consultar solo lee hechos guardados. Calcular este mes
							calcula el censo del período sin cambiar interruptores.
						</small>
					</div>
				</div>
			</CardHeader>
			<CardBody>
				<Row className='g-3 align-items-end mb-3'>
					<Col md={4} lg={3}>
						<MonthPickerInput
							id='usage-fact-period'
							label='Período'
							placeholder='Seleccione mes'
							valueFormat='MMMM YYYY'
							clearable
							value={periodToDate(year, month)}
							onChange={value => {
								const next = dateToPeriod(value);
								setYear(next.year);
								setMonth(next.month);
							}}
						/>
					</Col>
					<Col md={3}>
						<Button
							color='primary'
							onClick={handleConsult}
							disabled={!year || !month || isLoading}
						>
							Consultar
						</Button>
					</Col>
					<Col md={3}>
						<Button
							color='secondary'
							className='me-2'
							onClick={handleCalculate}
							disabled={!periodValid || ritualBusy}
						>
							{ritualBusy ? (
								<Spinner size='sm' className='me-2' />
							) : null}
							Calcular este mes
						</Button>
						<Link
							className={`btn btn-outline-primary${
								periodValid ? '' : ' disabled'
							}`}
							to={
								periodValid
									? buildPaymentGenerateHref(Number(year), Number(month))
									: '#'
							}
							aria-disabled={!periodValid}
						>
							Generar
						</Link>
					</Col>
				</Row>
				{nopStatus ? (
					<div className='form-check form-switch mb-3'>
						<Input
							className='form-check-input'
							type='checkbox'
							role='switch'
							id='usage-fact-include-nop'
							checked={includeNop}
							onChange={event => setIncludeNop(event.target.checked)}
						/>
						<Label
							className='form-check-label ms-2'
							htmlFor='usage-fact-include-nop'
						>
							{nopStatus.name}
						</Label>
					</div>
				) : null}
				{hasConsulted ? <UsedLegend /> : null}
				{censusIncomplete ? (
					<Alert color='warning' className='mb-3'>
						El censo ACT×4 está incompleto ({matrix.census.stored} de{' '}
						{matrix.census.expected}). Generar devolvería 400.
					</Alert>
				) : null}
				{periodError ? (
					<Alert color='danger' className='mb-3'>
						{periodError}
					</Alert>
				) : null}
				{refreshErrorDetail ? (
					<Alert color='warning' className='mb-3'>
						{refreshErrorDetail}
					</Alert>
				) : null}
				<div className='form-check mb-3'>
					<Input
						type='checkbox'
						className='form-check-input'
						id='usage-fact-include-sma'
						checked={includeSma}
						onChange={event => setIncludeSma(event.target.checked)}
					/>
					<Label
						className='form-check-label'
						htmlFor='usage-fact-include-sma'
					>
						Incluir previsualización SMA (interruptores vigentes, no
						sobrescribe REGULAR)
					</Label>
				</div>
				{errorDetail ? (
					<Alert color='danger' className='mb-3'>
						{errorDetail}
					</Alert>
				) : null}
				{previewStatus === 'error' ? (
					<Alert color='danger' fade={false}>
						No se pudo cargar la previsualización de cobro. Las
						descripciones quedan vacías.
					</Alert>
				) : null}
				{previewStatus === 'ok' && previewHasDuplicates ? (
					<Alert color='danger' fade={false}>
						Hay nombres duplicados en la previsualización. Las
						descripciones coincidentes se omitieron.
					</Alert>
				) : null}
				{paymentPreview ? (
					<div className='mb-3'>
						<Label className='form-label'>
							Previsualización REGULAR del servidor
						</Label>
						<pre
							className='bg-light p-2 small mb-0'
							style={{ maxHeight: 240, overflow: 'auto' }}
						>
							{JSON.stringify(paymentPreview, null, 2)}
						</pre>
					</div>
				) : null}
				{smaPreview ? (
					<div className='mb-3'>
						<Label className='form-label'>
							Previsualización SMA (local)
						</Label>
						<pre
							className='bg-light p-2 small mb-0'
							style={{ maxHeight: 240, overflow: 'auto' }}
						>
							{JSON.stringify(smaPreview, null, 2)}
						</pre>
					</div>
				) : null}
				{isLoading ? (
					<div className='text-center py-4'>
						<Spinner color='primary' />
					</div>
				) : !hasConsulted ? (
					<p className='text-muted mb-0'>
						Seleccione un período y pulse Consultar.
					</p>
				) : groups.length === 0 ? (
					<p className='text-muted mb-0'>
						No hay establecimientos para mostrar.
					</p>
				) : (
					<div className='table-responsive table-card'>
						<Table className='table-hover table-striped align-middle table-nowrap mb-0'>
							<thead>
								<tr>
									<th scope='col'>Centro</th>
									<th scope='col'>Estado</th>
									<th scope='col' className='text-center'>
										Base
									</th>
									{matrix.columns.map(column => (
										<th
											key={column.moduleId}
											scope='col'
											className='text-center'
										>
											{headerCounts[column.name] == null
												? column.name
												: `${column.name} (${headerCounts[column.name]})`}
										</th>
									))}
									<th scope='col'>Descripción</th>
								</tr>
							</thead>
							<tbody>
								{groups.map(group => (
									<React.Fragment
										key={group.companyId || group.companyName || 'company'}
									>
										<tr className='table-light'>
											<th scope='colgroup' colSpan={columnCount}>
												{group.companyName || group.companyId}
											</th>
										</tr>
										{group.rows.map(row => {
											const description = billedDescription(row.name);
											const item = billedItem(row.name);
											const hasExpand =
												item &&
												(item.quantity != null || item.total_price != null);
											return (
												<tr key={row.establishmentId}>
													<td>{row.name}</td>
													<td>
														<EstablishmentStatusBadge
															statusCode={row.statusCode}
															statusName={row.statusName}
															disabled={row.disabled}
														/>
													</td>
													<td className='text-center'>
														<UsedBadge value={row.base} />
													</td>
													{matrix.columns.map(column => (
														<td
															key={`${row.establishmentId}_${column.moduleId}`}
															className='text-center'
														>
															<UsedBadge
																value={
																	row.usedByModuleId[column.moduleId]
																}
															/>
														</td>
													))}
													<td>
														{description}
														{hasExpand ? (
															<details>
																<summary>Detalle</summary>
																{item.quantity != null ? (
																	<div>Cantidad: {item.quantity}</div>
																) : null}
																{item.total_price != null ? (
																	<div>Total: {item.total_price}</div>
																) : null}
															</details>
														) : null}
													</td>
												</tr>
											);
										})}
									</React.Fragment>
								))}
							</tbody>
						</Table>
					</div>
				)}
			</CardBody>
		</Card>
	);
};

export default ModuleUsageFactPreview;
